import PropTypes from 'prop-types';
import Link from 'next/link';
import getConfig from 'next/config';
import cls from 'classnames';

import { getContent, isWHEnv, isDDEnv } from '@/utils/helper';
import {
  analytics,
  getClickFooterNavigation,
  getClickSocialDetail,
} from '@/utils/analytics';

import Icon from '@/atoms/Icon/Icon';
import WestholmeLogo from '@/atoms/Logo/WestholmeLogo';
import DDLogo from '@/atoms/Logo/DDLogo';
import { normalizedUrl } from '@/molecules/CTA/CTA.constants';

const { publicRuntimeConfig = {} } = getConfig() || {};

function Footer({ entries, fields, showVersion }) {
  const {
    name,
    navigation,
    showLogo,
    copyrightText,
    socialLinks: socials,
    footerLinks: footerUrls,
  } = fields;

  const navLinks = navigation
    ? getContent(navigation.sys.id, entries)?.fields.links.map((link) =>
        getContent(link.sys.id, entries)
      )
    : [];
  const socialLinks = socials?.length
    ? socials.map((social) => getContent(social.sys.id, entries))
    : [];

  const footerLinks = footerUrls
    ? getContent(footerUrls.sys.id, entries)?.fields.links.map((link) =>
        getContent(link.sys.id, entries)
      )
    : [];

  const isWH = isWHEnv();
  const isDD = isDDEnv();

  const navLinksWH = [...navLinks, {fields: { name: "portal", url: process.env.NEXT_PUBLIC_PORTAL_LINK }}]
  const navLinksNew = isWH ? navLinksWH : navLinks;



  return (
    <div className="flex flex-col">
      <div className="hidden dd-kr:block dd-en:block bg-quality-gold h-[4.31px] w-full" />
      <div
        className="aaco-footer hidden-print mt-0 py-6 md:py-9 px-5 md:px-0 md:dd-kr:px-[30px] md:dd-en:px-[30px] bg-acacia dd-en:bg-progressive-blue dd-kr:bg-progressive-blue text-white"
        aria-label={name}
      >
        <div className="container mx-auto dd-kr:max-w-full dd-en:max-w-full dd-kr:mx-0 dd-en:mx-0 dd-kr:px-0 dd-en:px-0">
          {navLinksNew.length && (
            <div className="aaco-footer__navigation mb-11 dd-en:mb-8 md:dd-en:mb-[54px] dd-kr:mb-8 md:dd-kr:mb-[54px]">
              <ul className="mb-0 p-0 flex flex-col justify-center items-center md:flex-row">
                {navLinksNew.map((link, index) => {
                  const pageUrl =
                    link.fields?.page &&
                    normalizedUrl(
                      getContent(link.fields?.page.sys.id, entries)?.fields
                        ?.slug
                        ? getContent(link.fields?.page.sys.id, entries)?.fields
                            ?.slug
                        : '/'
                    );

                  return (
                    <li
                      key={index}
                      className={isDD ? "block mb-[22px] md:mb-0 md:px-10 " : `md:before:hidden before:absolute before:h-[1px] before:w-4 before:bg-warm-grey before:left-[48%] leading-[3] md:leading-[1px] block md:mb-0 w-full text-center ${index === 0 ? "" : "md:border-l-1"} border-solid border-warm-grey ${index === navLinksNew.length - 1 ? ' md:after:hidden after:absolute after:mt-[3rem] after:left-[48%] after:h-[1px] after:w-4 after:bg-warm-grey' : ''}`}
                    >
                      {link.fields?.page ? (
                        <Link href={pageUrl} passHref>
                          <a
                            className="text-body text-warm-grey dd-kr:text-white dd-en:text-white dd-kr:text-small dd-en:text-small uppercase dd-kr:normal-case dd-en:normal-case	font-button dd-kr:font-small dd-en:font-small dd-kr:font-text dd-en:font-text hover:underline"
                            onClick={(e) =>
                              analytics({
                                window,
                                document,
                                detail: getClickFooterNavigation(e, {
                                  url: pageUrl,
                                }),
                              })
                            }
                          >
                            {link.fields?.name}
                          </a>
                        </Link>
                      ) : (
                        <a
                          href={link.fields?.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-body text-warm-grey dd-kr:text-white dd-en:text-white dd-kr:text-small dd-en:text-small uppercase dd-kr:normal-case dd-en:normal-case	font-button dd-kr:font-small dd-en:font-small dd-kr:font-text dd-en:font-text hover:underline"
                          onClick={(e) =>
                            analytics({
                              window,
                              document,
                              detail: getClickFooterNavigation(e, {
                                url: link.fields?.url,
                              }),
                            })
                          }
                        >
                          {link.fields?.name}
                        </a>
                      )}
                    </li>
                  );
                })}
                <li className="hidden dd-kr:block dd-en:block mb-[22px]
                  md:mb-0 md:px-10 "
                  key="portal"
                >
                  <a href={process.env.NEXT_PUBLIC_PORTAL_LINK} target="_blank" rel="noopener noreferrer" className="text-button dd-kr:text-small dd-en:text-small uppercase dd-kr:normal-case dd-en:normal-case font-button dd-kr:font-small dd-en:font-small dd-kr:font-text dd-en:font-text hover:underline">
                    Portal
                  </a>
                </li>
              </ul>
            </div>
          )}
          {!!showLogo && (
            <>
              <div className="aaco-footer__logo flex md:py-[33px] justify-center md:mb-10 dd-kr:hidden dd-en:hidden border-t border-b">
                <WestholmeLogo color="#ffffffff" />
              </div>
              <div className="aaco-footer__logo hidden dd-kr:flex dd-en:flex justify-center mb-6 md:mb-[90px] ">
                <DDLogo width={100} height={116} />
              </div>
            </>
          )}
          <div className="aaco-footer__bottom">
            <div className={cls(
              "flex lg:grid lg:grid-cols-12 gap-x-2 lg:gap-x-10 flex-wrap", {
                "!flex-col md:!flex-row !gap-y-5 pt-[33px]": isWH,
              }
            )}>
              <div className="aaco-footer__social order-2 dd-en:order-1 dd-kr:order-1 md:dd-kr:order-2 md:dd-en:order-2 md:order-2 md:col-span-2 w-full dd-en:mb-[60px] dd-kr:mb-[60px] md:my-0 md:dd-kr:mb-0 md:dd-en:mb-0">
                {socialLinks.length && (
                  <ul className="mb-0 p-0 flex justify-center items-center">
                    {socialLinks.map((link, index) => (
                      <li key={index} className="block mx-2">
                        <a
                          href={link.fields?.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={link.fields?.name}
                          onClick={(e) => {
                            analytics({
                              window,
                              document,
                              detail: getClickSocialDetail(e, {
                                url: link.fields?.link,
                                platform: link.fields?.name,
                              }),
                            });
                          }}
                          className="flex flex-col w-8 h-8 bg-transparent border-1 dd-kr:border-40-white-on-dark-blue dd-en:border-40-white-on-dark-blue text-white justify-center items-center rounded-full p-2"
                        >
                          <Icon
                            name={link.fields?.icon}
                            className="block"
                            width={15}
                            height={15}
                          />
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="aaco-footer__copyright flex flex-col md:flex-row items-center md:col-span-5 dd-en:order-2 dd-kr:order-2 order-1 md:order-1 md:dd-kr:order-1 md:dd-en:order-1 w-full flex-1 justify-center md:justify-start">
                <div
                  className={cls('block md:hidden mr-2', {
                    'order-2': isDD,
                    'order-4 hidden md:order-none': isWH,
                  })}
                >
                  {footerLinks.length > 0 && (
                    <ul className="mb-0 p-0 flex flex-col justify-center items-center md:flex-row">
                      {footerLinks.map((link, index) => {
                        let p = null;
                        if (link.fields?.page) {
                          p = getContent(link.fields.page.sys.id, entries);
                        }
                        return (
                          <li
                            key={index}
                            className="block text-button underline"
                          >
                            {link.fields?.page ? (
                              <Link
                                href={normalizedUrl(p?.fields.slug)}
                                passHref
                              >
                                <a className="text-white dd-en:text-40-white-on-dark-blue dd-kr:text-40-white-on-dark-blue dd-kr:font-text dd-en:font-text dd-kr:text-small dd-en:text-small">
                                  {link.fields?.name}
                                </a>
                              </Link>
                            ) : (
                              <a
                                href={link.fields?.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-white dd-en:text-40-white-on-dark-blue dd-kr:text-40-white-on-dark-blue dd-kr:font-text dd-en:font-text dd-kr:text-small dd-en:text-small"
                              >
                                {link.fields?.name}
                              </a>
                            )}
                          </li>
                        );
                      })}
                      <li
                        key="portal"
                        className="text-button underline hidden dd-kr:hidden dd-en:hidden dd-kr:md:block dd-en:md:block "
                      >
                        <a
                          href={process.env.NEXT_PUBLIC_PORTAL_LINK}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-majesty-30 dd-en:text-40-white-on-dark-blue dd-kr:text-40-white-on-dark-blue dd-kr:font-text dd-en:font-text dd-kr:text-small dd-en:text-small"
                        >
                          Portal
                        </a>
                      </li>
                    </ul>
                  )}
                </div>

                <p
                  className={cls(
                    {
                      'order-2 md:order-none mb-0': isWH,
                      'order-1 mb-[7.38px]': isDD,
                    },
                    {
                      'font-text text-body text-warm-grey': isWH,
                      'font-text text-small text-40-white-on-dark-blue': isDD,
                    }
                  )}
                >
                  {copyrightText}
                  {showVersion && (
                    <small>&nbsp;&nbsp;v{publicRuntimeConfig?.version}</small>
                  )}
                </p>
              </div>

              <div
                className={cls(
                  'aaco-footer__links',
                  'md:col-span-5 justify-end hidden md:flex',
                  'order-1 md:order-3',
                  {
                    '!order-3 !flex !justify-between md:!justify-end': isWH,
                  }
                )}
              >
                {footerLinks.length > 0 && (
                  <ul className="mb-0 p-0 hidden md:flex dd-en:flex dd-kr:flex flex-col justify-center items-center md:flex-row">
                    {footerLinks.map((link, index) => {
                      let p = null;
                      if (link.fields?.page) {
                        p = getContent(link.fields.page.sys.id, entries);
                      }
                      return (
                        <li
                          key={index}
                          className="block text-button underline mx-5"
                        >
                          {link.fields?.page ? (
                            <Link href={normalizedUrl(p?.fields.slug)} passHref>
                              <a className="text-[13px] text-white dd-en:text-40-white-on-dark-blue dd-kr:text-40-white-on-dark-blue dd-kr:font-text dd-en:font-text dd-kr:text-small dd-en:text-small">
                                {link.fields?.name}
                              </a>
                            </Link>
                          ) : (
                            <a
                              href={link.fields?.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[13px] text-white font-sans dd-en:text-40-white-on-dark-blue dd-kr:text-40-white-on-dark-blue dd-kr:font-text dd-en:font-text dd-kr:text-small dd-en:text-small"
                            >
                              {link.fields?.name}
                            </a>
                          )}
                        </li>
                      );
                    })}
                    <li className="text-button underline hidden dd-kr:block dd-en:block">
                      <a
                        href={process.env.NEXT_PUBLIC_PORTAL_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cls("text-[13px] text-white font-sans dd-en:text-40-white-on-dark-blue dd-kr:text-40-white-on-dark-blue dd-kr:font-text dd-en:font-text dd-kr:text-small dd-en:text-small", {
                          "hidden": isWH,
                          "hidden md:block": isDD

                        })}
                      >
                        Portal
                      </a>
                    </li>
                  </ul>
                )}
                {footerLinks.length > 0 && footerLinks.map((link, index) => {
                    let p = null;
                    if (link.fields?.page) {
                      p = getContent(link.fields.page.sys.id, entries);
                    }
                  return (
                    <Link href={normalizedUrl(p?.fields.slug)} key={index} passHref>
                      <a className={cls(
                        "text-[13px] text-white dd-en:text-40-white-on-dark-blue dd-kr:text-40-white-on-dark-blue dd-kr:font-text dd-en:font-text dd-kr:text-small dd-en:text-small underline", {
                          "block md:hidden": isWH,
                          "hidden": isDD
                        }
                      )}>
                        {link.fields?.name}
                      </a>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// @TODO: define real props for the Footer based on it requirements
Footer.propTypes = {
  entries: PropTypes.array,
  fields: PropTypes.object,
};

Footer.defaultProps = {
  entries: [],
  fields: null,
};

export default Footer;
